import React, {useEffect, useState} from 'react';
import {IconButton, ListItemButton, ListItemText, Tooltip} from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useNavigate, useParams} from "react-router";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// Convert timestamp to human-readable time
// Shows relative time if < 1 month, otherwise shows absolute date
function getTimePassed(timestamp) {
    if (!timestamp) return '';

    // Handle Firestore Timestamp objects
    const date = timestamp?.toDate?.() || new Date(timestamp);
    const now = dayjs();
    const messageTime = dayjs(date);

    const monthsAgo = now.diff(messageTime, 'month');

    if (monthsAgo >= 1) {
        // More than a month: show absolute date
        return messageTime.format('MMM D, YYYY');
    } else {
        // Less than a month: show relative time
        return messageTime.fromNow();
    }
}

const ChatItem = ({chat, idx, onClick, onDelete}) => {
    const navigate = useNavigate();
    const {chatId} = useParams();
    const title = chat?.lastMessage?.text || chat?.title || 'New chat';
    const timestamp = chat?.lastMessage?.createdAt;
    const [hovered, setHovered] = useState(false);

    // tick updates every minute to refresh relative time strings
    const [, setTick] = useState(Date.now());

    useEffect(() => {
        if (!timestamp) return; // nothing to watch

        // Convert to dayjs and check if it's within 1 month; if older, no need to update every minute
        const date = timestamp?.toDate?.() || new Date(timestamp);
        const monthsAgo = dayjs().diff(dayjs(date), 'month');

        if (monthsAgo >= 1) return; // static display

        // start interval to update every minute
        setTick(Date.now()); // immediate refresh
        const id = setInterval(() => setTick(Date.now()), 60 * 1000);
        return () => clearInterval(id);
    }, [timestamp]);

    // Recompute displayed time when timestamp or tick changes (tick triggers re-render)
    const timeElapsed = getTimePassed(timestamp);

    const handleClick = () => {
        if (onClick) {
            onClick(chat);
        } else {
            navigate(chat.id);
        }
    };

    const isSelected = chatId != null && String(chat?.id) === String(chatId);

    // If there's no chatId in the URL and this is the first item (idx === 0), navigate to it.
    useEffect(() => {
        if (!chatId && idx === 0 && chat?.id) {
            // replace so we don't create an extra history entry on initial auto-select
            navigate(chat.id, {replace: true});
        }
    }, [chatId, idx, chat?.id, navigate]);

    const handleDelete = (e) => {
        e.stopPropagation();
        if (onDelete) onDelete(chat);
    };

    return (
        <ListItemButton
            onClick={handleClick}
            selected={isSelected}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{pr: 0.5}}
        >
            <ListItemText
                primary={title}
                secondary={timeElapsed}
                primaryTypographyProps={{noWrap: true, fontSize: '0.875rem'}}
            />
            {(hovered || isSelected) && onDelete && (
                <Tooltip title="Delete thread" placement="right">
                    <IconButton
                        size="small"
                        onClick={handleDelete}
                        sx={{ml: 0.5, flexShrink: 0, color: 'text.secondary', '&:hover': {color: 'error.main'}}}
                    >
                        <DeleteOutlineIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            )}
        </ListItemButton>
    );
};

export default React.memo(ChatItem);
